import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import Breadcrumb from '@/components/layout/Breadcrumb'
import CitySelector from '@/components/prayer/CitySelector'
import PrayerTimesCard from '@/components/prayer/PrayerTimesCard'
import PrayerCountdown from '@/components/prayer/PrayerCountdown'
import PrayerScheduleTable from '@/components/prayer/PrayerScheduleTable'
import PrayerCalendar from '@/components/prayer/PrayerCalendar'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { prayerTimesApi, type City } from '@/services/prayerTimesApiService'
import { mockPrayerTimes } from '@/services/mockDataService'

interface CityLocal {
  id: string
  name: string
  country: string
  coordinates: { lat: number; lng: number }
}

interface PrayerTimesResponseLocal {
  prayers: { name: string; time: string }[]
}

interface AladhanTimingsResponse {
  code: number
  status: string
  data: {
    timings: {
      Fajr: string
      Sunrise: string
      Dhuhr: string
      Asr: string
      Maghrib: string
      Isha: string
      [key: string]: string
    }
  }
}

interface AladhanCalendarResponse {
  code: number
  status: string
  data: {
    date: {
      gregorian: { date: string } // "17-11-2025"
    }
    timings: {
      Fajr: string
      Sunrise: string
      Dhuhr: string
      Asr: string
      Maghrib: string
      Isha: string
      [key: string]: string
    }
  }[]
}

export interface CalendarDay {
  date: Date
  prayers: {
    name: string
    time: string
  }[]
}

// دالة تشيل الـ (EET) وغيره من الوقت
const normalizeTime = (t: string) => t.split(' ')[0]

// تحسب الصلاة القادمة فعلاً حسب الوقت الآن
function getNextPrayerFromList(
  prayers: { name: string; time: string }[]
): { name: string; time: string } | null {
  const now = new Date()
  let next: { name: string; time: string } | null = null
  let nextDate: Date | null = null

  for (const p of prayers) {
    const [hh, mm] = p.time.split(':').map(Number)
    if (Number.isNaN(hh) || Number.isNaN(mm)) continue

    const d = new Date(now)
    d.setHours(hh, mm, 0, 0)

    if (d > now) {
      if (!nextDate || d < nextDate) {
        next = p
        nextDate = d
      }
    }
  }

  // لو اليوم خلص، رجّع أول صلاة (فجر بكرة تقريباً)
  if (!next && prayers.length > 0) {
    next = prayers[0]
  }

  return next
}

export default function PrayerTimesPage() {
  const { t } = useTranslation()
  const [selectedCity, setSelectedCity] = useState<CityLocal | null>(null)
  const [cities, setCities] = useState<City[]>([])
  const [prayerTimes, setPrayerTimes] = useState<PrayerTimesResponseLocal | null>(null)
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const today = new Date()

  // دالة لجلب الموقع من IP
  const getLocationFromIP = async (): Promise<{ city: string; country: string; lat: number; lng: number } | null> => {
    // محاولة استخدام ipapi.co أولاً
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000) // timeout بعد 5 ثواني
      
      const response = await fetch('https://ipapi.co/json/', {
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      
      if (!response.ok) throw new Error('Failed to fetch location')
      
      const data = await response.json()
      
      if (data.error) {
        console.warn('IP location error from ipapi.co:', data.reason)
        throw new Error(data.reason || 'IP API error')
      }

      if (data.city && data.latitude && data.longitude) {
        console.log('Successfully got location from ipapi.co:', data.city, data.country_name)
        return {
          city: data.city || '',
          country: data.country_name || '',
          lat: data.latitude || 0,
          lng: data.longitude || 0,
        }
      }
    } catch (err) {
      console.warn('ipapi.co failed:', err)
    }

    // محاولة استخدام geojs.io كبديل
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      
      const response = await fetch('https://get.geojs.io/v1/ip/geo.json', {
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      
      if (!response.ok) throw new Error('Failed to fetch location')
      
      const data = await response.json()
      
      if (data.city && data.latitude && data.longitude) {
        console.log('Successfully got location from geojs.io:', data.city, data.country)
        return {
          city: data.city || '',
          country: data.country || '',
          lat: parseFloat(data.latitude) || 0,
          lng: parseFloat(data.longitude) || 0,
        }
      }
    } catch (err) {
      console.error('geojs.io also failed:', err)
    }

    return null
  }

  // خريطة أسماء المدن الشائعة (الإنجليزية -> العربية)
  const cityNameMap: Record<string, string> = {
    'cairo': 'القاهرة',
    'alexandria': 'الإسكندرية',
    'giza': 'الجيزة',
    'riyadh': 'الرياض',
    'jeddah': 'جدة',
    'mecca': 'مكة المكرمة',
    'makkah': 'مكة المكرمة',
    'medina': 'المدينة المنورة',
    'madina': 'المدينة المنورة',
    'dammam': 'الدمام',
    'khobar': 'الخبر',
  }

  // دالة لحساب المسافة بين نقطتين باستخدام Haversine formula (بالكيلومتر)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371 // نصف قطر الأرض بالكيلومتر
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLon = ((lon2 - lon1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  // دالة للبحث عن أقرب مدينة أو مطابقة الاسم
  const findMatchingCity = (
    locationCity: string,
    locationLat: number,
    locationLng: number,
    availableCities: City[]
  ): City | null => {
    if (!locationCity && (!locationLat || !locationLng)) return null

    // أولاً: محاولة المطابقة بالاسم (بالعربي أو الإنجليزي)
    const cityNameLower = locationCity.toLowerCase().trim()
    
    // البحث في خريطة الأسماء أولاً
    const mappedArabicName = cityNameMap[cityNameLower]
    
    const matchedByName = availableCities.find((city) => {
      const cityNameAr = city.name.toLowerCase().trim()
      const cityNameEn = city.nameEn?.toLowerCase().trim() || ''
      
      return (
        cityNameAr === cityNameLower ||
        cityNameEn === cityNameLower ||
        cityNameAr === mappedArabicName ||
        cityNameEn === cityNameLower ||
        cityNameAr.includes(cityNameLower) ||
        cityNameLower.includes(cityNameAr) ||
        (mappedArabicName && cityNameAr === mappedArabicName)
      )
    })

    if (matchedByName) {
      console.log('Matched city by name:', matchedByName.name, 'from IP city:', locationCity)
      return matchedByName
    }

    // ثانياً: البحث عن أقرب مدينة بناءً على الإحداثيات
    if (locationLat && locationLng && locationLat !== 0 && locationLng !== 0) {
      let closestCity: City | null = null
      let minDistance = Infinity

      availableCities.forEach((city) => {
        const distance = calculateDistance(locationLat, locationLng, city.latitude, city.longitude)
        if (distance < minDistance) {
          minDistance = distance
          closestCity = city
        }
      })

      // إذا كانت المسافة أقل من 50 كم، استخدم المدينة
      if (closestCity && minDistance < 50) {
        console.log('Matched city by coordinates:', closestCity.name, 'distance:', minDistance.toFixed(2), 'km')
        return closestCity
      } else if (closestCity) {
        console.log('Closest city found but too far:', closestCity.name, 'distance:', minDistance.toFixed(2), 'km')
      }
    }

    console.log('No matching city found for:', locationCity, 'at', locationLat, locationLng)
    return null
  }

  // 1) تحميل المدن وجلب الموقع من IP
  useEffect(() => {
    const fetchCitiesAndLocation = async () => {
      try {
        setIsLoading(true)
        
        // تحميل المدن
        const data = await prayerTimesApi.getCities()
        setCities(data)

        if (data.length === 0) {
          setError('فشل تحميل قائمة المدن')
          return
        }

        // محاولة جلب الموقع من IP
        const location = await getLocationFromIP()
        
        if (location && location.city) {
          console.log('Location from IP:', location)
          
          // البحث عن المدينة المطابقة
          const matchedCity = findMatchingCity(location.city, location.lat, location.lng, data)
          
          if (matchedCity) {
            console.log('Setting city to:', matchedCity.name)
            setSelectedCity({
              id: matchedCity.id.toString(),
              name: matchedCity.name,
              country: location.country || 'Egypt',
              coordinates: { lat: matchedCity.latitude, lng: matchedCity.longitude },
            })
            return
          } else {
            console.warn('Could not match city from IP location:', location.city)
          }
        } else {
          console.warn('Could not get location from IP')
        }

        // إذا لم يتم العثور على مطابقة، استخدم أول مدينة في القائمة
        const firstCity = data[0]
        setSelectedCity({
          id: firstCity.id.toString(),
          name: firstCity.name,
          country: 'Egypt',
          coordinates: { lat: firstCity.latitude, lng: firstCity.longitude },
        })
      } catch (err) {
        console.error('Failed to fetch cities:', err)
        setError('فشل تحميل قائمة المدن')
        
        // Fallback: استخدام أول مدينة من fallback cities
        const fallbackCities = [
          { id: '1', name: 'القاهرة', nameEn: 'Cairo', latitude: 30.0444, longitude: 31.2357 },
        ]
        if (fallbackCities.length > 0) {
          const firstCity = fallbackCities[0]
          setSelectedCity({
            id: firstCity.id.toString(),
            name: firstCity.name,
            country: 'Egypt',
            coordinates: { lat: firstCity.latitude, lng: firstCity.longitude },
          })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchCitiesAndLocation()
  }, [])

  // 2) جلب أوقات اليوم + تقويم 30 يوم من Aladhan لما المدينة تتغيّر
  useEffect(() => {
    if (!selectedCity) return

    const fetchPrayerData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // --------- أوقات اليوم الواحد ---------
        const timingsUrl = `https://api.aladhan.com/v1/timingsByCity?city=${encodeURIComponent(
          selectedCity.name
        )}&country=${encodeURIComponent('Egypt')}&method=5&school=1`

        const res = await fetch(timingsUrl)
        if (!res.ok) throw new Error('Network error')

        const json: AladhanTimingsResponse = await res.json()
        if (json.code !== 200) throw new Error(json.status || 'API error')

        const t = json.data.timings

        const prayersToday: { name: string; time: string }[] = [
          { name: 'fajr', time: normalizeTime(t.Fajr) },
          { name: 'sunrise', time: normalizeTime(t.Sunrise) },
          { name: 'dhuhr', time: normalizeTime(t.Dhuhr) },
          { name: 'asr', time: normalizeTime(t.Asr) },
          { name: 'maghrib', time: normalizeTime(t.Maghrib) },
          { name: 'isha', time: normalizeTime(t.Isha) },
        ]

        setPrayerTimes({ prayers: prayersToday })

        // --------- تقويم الشهر (30 يوم) ---------
        const now = new Date()
        const month = now.getMonth() + 1
        const year = now.getFullYear()

        const calendarUrl = `https://api.aladhan.com/v1/calendarByCity?city=${encodeURIComponent(
          selectedCity.name
        )}&country=${encodeURIComponent('Egypt')}&method=5&school=1&month=${month}&year=${year}`

        const calRes = await fetch(calendarUrl)
        if (!calRes.ok) throw new Error('Calendar network error')

        const calJson: AladhanCalendarResponse = await calRes.json()
        if (calJson.code !== 200) throw new Error(calJson.status || 'Calendar API error')

        const days: CalendarDay[] = calJson.data.map((d) => {
          // d.date.gregorian.date شكلها "17-11-2025"
          const [dayStr, monthStr, yearStr] = d.date.gregorian.date.split('-')
          const dayNum = parseInt(dayStr, 10)
          const monthIndex = parseInt(monthStr, 10) - 1
          const yearNum = parseInt(yearStr, 10)

          const dateObj = new Date(yearNum, monthIndex, dayNum)

          // Sort prayers: Fajr first, Isha last
          const prayersList = [
            { name: 'fajr', time: normalizeTime(d.timings.Fajr) },
            { name: 'sunrise', time: normalizeTime(d.timings.Sunrise) },
            { name: 'dhuhr', time: normalizeTime(d.timings.Dhuhr) },
            { name: 'asr', time: normalizeTime(d.timings.Asr) },
            { name: 'maghrib', time: normalizeTime(d.timings.Maghrib) },
            { name: 'isha', time: normalizeTime(d.timings.Isha) },
          ]
          const fajr = prayersList.find(p => p.name === 'fajr')
          const isha = prayersList.find(p => p.name === 'isha')
          const others = prayersList.filter(p => p.name !== 'fajr' && p.name !== 'isha')
          const sortedPrayers = []
          if (fajr) sortedPrayers.push(fajr)
          sortedPrayers.push(...others)
          if (isha) sortedPrayers.push(isha)
          
          return {
            date: dateObj,
            prayers: sortedPrayers,
          }
        })

        setCalendarDays(days)
      } catch (err) {
        console.error('Failed to fetch prayer data:', err)
        setError(t('prayer.loadError'))
        setPrayerTimes(null)
        setCalendarDays([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchPrayerData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity])

  // 3) تجهيز بيانات الجدول والـ cards

  // Sort prayers: Fajr first, Isha last, others in between
  const sortPrayers = (prayersList: Array<{ name: string; adhan: string; iqama: string }>) => {
    const fajr = prayersList.find(p => p.name === 'fajr')
    const isha = prayersList.find(p => p.name === 'isha')
    const others = prayersList.filter(p => p.name !== 'fajr' && p.name !== 'isha')
    
    const sorted: Array<{ name: string; adhan: string; iqama: string }> = []
    if (fajr) sorted.push(fajr)
    sorted.push(...others)
    if (isha) sorted.push(isha)
    
    return sorted.length > 0 ? sorted : prayersList
  }

  const prayers = sortPrayers(
    prayerTimes?.prayers && prayerTimes.prayers.length > 0
      ? prayerTimes.prayers.map((p) => ({
          name: p.name,
          adhan: p.time,
          iqama: p.time,
        }))
      : mockPrayerTimes.prayers.map((p: { name: string; adhan: string; iqama: string }) => ({
          name: p.name,
          adhan: p.adhan,
          iqama: p.iqama,
        }))
  )

  type PrayerWithAdhanIqama = { name: string; adhan: string; iqama: string }
  const typedPrayers: PrayerWithAdhanIqama[] = prayers as PrayerWithAdhanIqama[]

  let nextPrayer =
    prayerTimes?.prayers && prayerTimes.prayers.length > 0
      ? getNextPrayerFromList(prayerTimes.prayers)
      : null

  if (!nextPrayer) {
    nextPrayer = {
      name: mockPrayerTimes.nextPrayer.name,
      time: mockPrayerTimes.nextPrayer.time,
    }
  }

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('common.services'), to: '/services/prayer' },
    { label: t('prayer.title') },
  ]

  return (
    <>
      <Helmet>
        <title>{t('prayer.title')} - {t('brand.name', 'Sakeena')}</title>
        <meta name="description" content={t('public.prayer.seo.description')} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('prayer.title')}</h1>
          <p className="text-lg text-gray-600">{t('public.prayer.intro')}</p>
        </div>

        {/* City Selector */}
        <div className="mb-6 max-w-md">
          {isLoading && !cities.length ? (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : (
            <CitySelector selectedCity={selectedCity} onCityChange={setSelectedCity} />
          )}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Prayer Times Card with Navigation */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex items-center gap-2 sm:gap-4 md:gap-6 mb-6">
            {/* Previous Day Navigation - Right side (RTL) */}
            <button
              className="flex flex-col items-center gap-1.5 sm:gap-2 group flex-shrink-0"
              onClick={() => {
                // Navigate to previous day - could be implemented if needed
                // For now, this is just a visual element
              }}
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center bg-white dark:bg-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all group-hover:scale-110 text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
              </div>
              <div className="text-center max-w-[100px] sm:max-w-[120px] md:max-w-[150px] hidden sm:block">
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-0.5 md:mb-1">{t('prayer.goTo')}</p>
                <p className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {t('prayer.yesterday')}
                </p>
              </div>
            </button>

            {/* Prayer Times Cards */}
            <div className="flex-1">
              <PrayerTimesCard
                date={today}
                nextPrayer={{
                  name: nextPrayer.name,
                  time: nextPrayer.time,
                  countdown: '',
                }}
              />
            </div>

            {/* Next Day Navigation - Left side (RTL) */}
            <button
              className="flex flex-col items-center gap-1.5 sm:gap-2 group flex-shrink-0"
              onClick={() => {
                // Navigate to next day - could be implemented if needed
                // For now, this is just a visual element
              }}
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center bg-white dark:bg-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all group-hover:scale-110 text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
              </div>
              <div className="text-center max-w-[100px] sm:max-w-[120px] md:max-w-[150px] hidden sm:block">
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-0.5 md:mb-1">{t('prayer.goTo')}</p>
                <p className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {t('prayer.dayAfterTomorrow')}
                </p>
              </div>
            </button>
          </div>
        )}

        {/* Countdown Card */}
        {nextPrayer && (
          <div className="mb-6">
            <PrayerCountdown
              nextPrayerTime={nextPrayer.time}
              prayerName={nextPrayer ? t(`prayer.prayerNames.${nextPrayer.name}`) : ''}
            />
          </div>
        )}

        {/* Schedule Table */}
        {!isLoading && (
          <div className="mb-6">
            <PrayerScheduleTable
              prayers={typedPrayers}
              nextPrayerName={nextPrayer?.name}
            />
          </div>
        )}

        {/* Calendar – بيانات حقيقية من Aladhan */}
        <PrayerCalendar days={calendarDays} />
      </div>
    </>
  )
}
